import { Dropdown } from '@components/atoms/Dropdown/Dropdown';
import { IconArrowCollapse } from '@src/assets/icon-arrow-collapse';
import type { CookiesProps } from '@vgn-medien-holding/vgn-fe-components';
import { Cookies as CookiesUnified, Spinner } from '@vgn-medien-holding/vgn-fe-components';

export interface CookiesCompleteProps extends Omit<CookiesProps, 'classProps'> {
  className?: string;
}

// Wrappers to match the expected interfaces
const DropdownWrapper = ({
  items,
  selectedItem,
  setSelectedItem,
  placeholder = '',
  className,
}: {
  items: Array<{ id: string; label: string }>;
  selectedItem: { id: string; label: string };
  setSelectedItem: (item: { id: string; label: string }) => void;
  placeholder?: string;
  className?: string;
}) => (
  <Dropdown
    items={items}
    selectedItem={selectedItem}
    setSelectedItem={(item) => setSelectedItem(item as { id: string; label: string })}
    placeholder={placeholder}
    buttonAfterSlot={<IconArrowCollapse classProps={{ root: 'text-white size-5' }} />}
    classProps={{
      button: `${className} bg-black text-white rounded-full px-6 py-2 border border-gray-500 hover:bg-black hover:text-white`,
      buttonPlaceholder: 'text-white',
      buttonItemSelected: 'text-white',
      buttonActive: 'bg-black text-white',
    }}
  />
);

const SpinnerWrapper = ({ className }: { className?: string }) => <Spinner classProps={{ root: className }} />;

export const CookiesComplete = ({ className, ...props }: CookiesCompleteProps) => {
  const classProps = {
    root: `${className || ''}`,
    container: 'article-padding-default mx-auto max-w-content-default py-8',
    header: 'mb-8',
    title: 'heading-1 mb-4',
    description: 'body-base text-gray-dark',

    // Loading states
    loadingContainer: 'flex items-center justify-center py-12',
    loadingSpinner: 'mx-auto mb-4',
    loadingText: 'text-sm text-gray-medium',

    // Statistics section
    statsSection: 'mb-8',
    statsGrid: 'grid grid-cols-3 gap-4',
    statsCard: 'rounded-lg bg-white p-6 text-center',
    statsValue: 'text-3xl font-bold text-black',
    statsLabel: 'mt-1 text-sm text-black',

    // Filter section
    filterSection: 'relative z-20 mb-8',
    filterTitle: 'heading-2 mb-4 text-white',
    filterContainer: 'rounded-lg bg-white p-6 flex flex-col gap-4 lg:flex-row lg:gap-4',
    filterColumn: 'flex-1 min-w-0',
    filterLabel: 'mb-2 block text-sm font-semibold text-black',
    filterInput:
      'w-full rounded-md border border-black bg-white/10 px-4 py-2 text-black placeholder:text-black focus:border-secondary focus:bg-white/20 focus:outline-none focus:ring-2 focus:ring-secondary',
    filterDropdown: 'w-full bg-white/10 border-white/20 text-black hover:bg-white/20',
    filterResults: 'mt-4 text-sm text-white font-medium',

    // Table section
    tableSection: 'mb-8',
    tableTitle: 'heading-2 mb-4 text-black',
    tableContainer: 'overflow-x-auto rounded-lg border border-gray bg-white',
    table: 'w-full text-xs',
    thead: '',
    tbody: 'divide-y divide-gray',
    theadRow: 'border-b border-gray bg-gray-light',
    theadCell: 'px-4 py-3 text-left text-sm font-semibold text-black',
    bodyRow: 'transition-colors hover:bg-gray-light',
    bodyCell: 'px-4 py-3',

    // Vendor info
    vendorName: 'font-medium text-black',
    vendorSubtext: 'mt-1 text-xs text-black md:hidden',

    // Status badges
    statusBadge: 'inline-flex rounded-full px-2 py-1 text-xs font-medium',
    statusActive: 'bg-secondary/20 text-black',
    statusInactive: 'bg-gray-light text-black',

    // Data usage list
    dataList: 'space-y-1 text-xs',
    dataItem: 'text-black',
    expandButton: 'text-xs font-medium text-black hover:text-gray-dark',

    // Actions
    actionColumn: 'flex flex-col space-y-2',
    actionLink: 'text-xs text-black hover:text-gray-dark',

    // No data states
    noDataContainer: 'rounded-lg border border-gray bg-gray-light p-8 text-center',
    noDataText: 'text-black',
    noResultsContainer: 'rounded-lg border border-gray bg-gray-light p-8 text-center',
    noResultsText: 'text-black',

    // Popover
    popoverOverlay: 'fixed inset-0 z-50 flex items-center justify-center bg-black/50',
    popoverContainer: 'max-h-[80vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white',
    popoverHeader: 'flex items-center justify-between border-b border-gray-light p-6',
    popoverTitle: 'text-xl font-semibold text-black',
    popoverCloseButton: 'text-gray hover:text-black',
    popoverContent: 'max-h-[60vh] overflow-y-auto p-6',
    popoverSection: 'mb-6',
    popoverSectionTitle: 'mb-3 text-lg font-semibold text-black',
    popoverSectionContent: 'gap-4 rounded-lg bg-gray-light/50 p-4 text-black [&_*]:text-black [&_p]:mb-0 [&_h5]:mb-1',
    popoverTable: 'prose prose-sm grid max-w-none text-black [&_*]:text-black [&_p]:mb-0 [&_h5]:mb-1',
  };

  return (
    <CookiesUnified
      classProps={classProps}
      DropdownComponent={DropdownWrapper}
      SpinnerComponent={SpinnerWrapper}
      {...props}
    />
  );
};

export default CookiesComplete;
